import { useEffect, useMemo, useState } from 'react'
import { useWorkspace } from '../../../context/useWorkspace'
import type { AuditActivityCursor, WorkspaceActivityRecord } from '../../../services/activityService'
import type { DashboardChecklistVisualItem, DashboardVisualItem } from '../../../types/dashboardVisualItem'
import type { TimelineElement } from '../../../types/timelineElement'
import {
  checklistWeight,
  findChecklistRootGoalId,
  type SandColumn,
} from './useSandfallModel'

export type SandfallWindowDays = 7 | 30 | 90

export type SandfallColumnSnapshot = {
  goalId: string
  label: string
  remainingWeight: number
  totalWeight: number
  colorToken: string
}

export type SandfallHistoryPoint = {
  day: string
  columns: SandfallColumnSnapshot[]
}

export type SandfallHistoryState = {
  points: SandfallHistoryPoint[]
  isLoading: boolean
  hasActivity: boolean
}

const DAY_MS = 86_400_000
const COMPLETION_ACTIONS = new Set(['CHECKLIST_COMPLETED', 'CHECKLIST_FORCE_COMPLETED'])
const REOPEN_ACTIONS = new Set(['CHECKLIST_REOPENED'])
const TRACKED_ACTIONS = [
  'CHECKLIST_COMPLETED',
  'CHECKLIST_REOPENED',
  'CHECKLIST_FORCE_COMPLETED',
  'CHECKLIST_STATUS_CHANGED',
] as const

const toDayIso = (date: Date): string => date.toISOString().slice(0, 10)

const dateAtUtcDayStart = (dayIso: string): Date => new Date(`${dayIso}T00:00:00.000Z`)

const orderedDays = (windowDays: SandfallWindowDays, now: Date): string[] => {
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Array.from({ length: windowDays }, (_, index) =>
    toDayIso(new Date(todayStart - (windowDays - 1 - index) * DAY_MS)),
  )
}

const metadataString = (metadata: Record<string, unknown> | null, keys: readonly string[]): string | null => {
  if (!metadata) return null
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'string') return value
  }
  return null
}

const historyDeltaFor = (record: WorkspaceActivityRecord): number => {
  if (COMPLETION_ACTIONS.has(record.action)) return 1
  if (REOPEN_ACTIONS.has(record.action)) return -1
  if (record.action !== 'CHECKLIST_STATUS_CHANGED') return 0

  const previous = metadataString(record.metadata, ['previousStatus', 'previousWorkflowStatus', 'fromStatus', 'from'])
  const next = metadataString(record.metadata, ['nextStatus', 'nextWorkflowStatus', 'toStatus', 'to'])
  if (next === 'COMPLETED' && previous !== 'COMPLETED') return 1
  if (previous === 'COMPLETED' && next !== 'COMPLETED') return -1
  return 0
}

export const buildSandfallHistorySnapshots = (
  columns: readonly SandColumn[],
  checklistItems: readonly DashboardChecklistVisualItem[],
  events: readonly WorkspaceActivityRecord[],
  windowDays: SandfallWindowDays,
  now: Date = new Date(),
): SandfallHistoryPoint[] => {
  const days = orderedDays(windowDays, now)
  const daySet = new Set(days)
  const columnByGoalId = new Map(columns.map((column) => [column.goalId, column]))
  const levels = new Map(columns.map((column) => [column.goalId, column.remainingWeight]))
  const checklistById = new Map(checklistItems.map((item) => [item.id, item]))

  const eventsByDay = new Map<string, WorkspaceActivityRecord[]>()
  for (const event of events) {
    const day = event.createdAt ? toDayIso(event.createdAt) : null
    if (!day || !daySet.has(day)) continue
    const bucket = eventsByDay.get(day) ?? []
    bucket.push(event)
    eventsByDay.set(day, bucket)
  }

  const points: SandfallHistoryPoint[] = []
  for (let index = days.length - 1; index >= 0; index -= 1) {
    const day = days[index]
    points.push({
      day,
      columns: columns.map((column) => ({
        goalId: column.goalId,
        label: column.label,
        colorToken: column.colorToken,
        totalWeight: column.totalWeight,
        remainingWeight: levels.get(column.goalId) ?? column.remainingWeight,
      })),
    })

    const dayEvents = [...(eventsByDay.get(day) ?? [])].sort((left, right) =>
      (right.createdAt?.getTime() ?? 0) - (left.createdAt?.getTime() ?? 0),
    )
    for (const event of dayEvents) {
      const checklist = checklistById.get(event.targetId)
      if (!checklist?.rootGoalId || !columnByGoalId.has(checklist.rootGoalId)) continue
      const delta = historyDeltaFor(event)
      if (delta === 0) continue
      const column = columnByGoalId.get(checklist.rootGoalId)
      if (!column) continue
      const next = (levels.get(column.goalId) ?? column.remainingWeight) + delta * checklistWeight(checklist)
      levels.set(column.goalId, Math.min(column.totalWeight, Math.max(0, next)))
    }
  }

  return points.reverse()
}

const loadActivity = async (
  workspaceId: string,
  windowDays: SandfallWindowDays,
): Promise<WorkspaceActivityRecord[]> => {
  const [{ firestoreDb }, { queryWorkspaceActivityPage }] = await Promise.all([
    import('../../../services/firebase'),
    import('../../../services/activityService'),
  ])
  const to = new Date()
  const from = new Date(to.getTime() - (windowDays - 1) * DAY_MS)
  from.setHours(0, 0, 0, 0)
  const records: WorkspaceActivityRecord[] = []
  let cursor: AuditActivityCursor | null = null

  for (let page = 0; page < 8; page += 1) {
    const result = await queryWorkspaceActivityPage(
      firestoreDb,
      workspaceId,
      {
        from,
        to,
        memberIds: [],
        itemIds: [],
        activityTypes: [],
        actionTypes: [...TRACKED_ACTIONS],
      },
      cursor,
      250,
    )
    records.push(...result.records)
    if (!result.hasMore || !result.nextCursor) break
    cursor = result.nextCursor
  }

  return records
}

export const useSandfallHistory = (
  columns: readonly SandColumn[],
  visualItems: readonly DashboardVisualItem[],
  elements: readonly TimelineElement[],
  windowDays: SandfallWindowDays,
): SandfallHistoryState => {
  const { activeWorkspaceId } = useWorkspace()
  const checklistItems = useMemo(() => {
    const elementById = new Map(elements.map((element) => [element.id, element]))
    return visualItems
      .filter((item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST')
      .map((item) => ({
        ...item,
        rootGoalId: findChecklistRootGoalId(item, elementById),
      }))
  }, [elements, visualItems])

  const [events, setEvents] = useState<WorkspaceActivityRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!activeWorkspaceId || columns.length === 0) {
      setEvents([])
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    void loadActivity(activeWorkspaceId, windowDays)
      .then((records) => {
        if (!cancelled) setEvents(records)
      })
      .catch(() => {
        if (!cancelled) setEvents([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => { cancelled = true }
  }, [activeWorkspaceId, columns.length, windowDays])

  const points = useMemo(
    () => buildSandfallHistorySnapshots(columns, checklistItems, events, windowDays),
    [checklistItems, columns, events, windowDays],
  )

  return {
    points,
    isLoading,
    hasActivity: events.length > 0,
  }
}

export const sandfallWindowStart = (windowDays: SandfallWindowDays, now = new Date()): Date =>
  dateAtUtcDayStart(orderedDays(windowDays, now)[0])
